import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  Ontbinding,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, filter, merge, Observable, shareReplay, take} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-fiscale-eenheid-ontbinding',
  templateUrl: './fiscale-eenheid-ontbinding.component.html',
  styleUrls: ['./fiscale-eenheid-ontbinding.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class FiscaleEenheidOntbindingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ontbindingFg = new FormGroup({
    ontbinding: new FormArray<
      FormGroup<{
        naamDochter: FormControl<string | null>;
        ontbindingsdatum: FormControl<Date | null>;
        onbetaaldGeblevenSchulden: FormControl<number | null>,
      }>
    >([]),
  });

  get ontbinding(): FormArray<FormGroup<{
    naamDochter: FormControl<string | null>;
    ontbindingsdatum: FormControl<Date | null>;
    onbetaaldGeblevenSchulden: FormControl<number | null>,
  }>> {
    return this.ontbindingFg.controls.ontbinding;
  }

  addRij(): void {
    this.ontbinding.push(this.createFormGroup(), { emitEvent: false });
  }

  verwijderRij(index: number): void {
    this.ontbinding.removeAt(index, { emitEvent: false });
  }

  createFormGroup(item?: Ontbinding) {
    return new FormGroup({
      naamDochter: new FormControl<string | null>(item?.naamDochter ?? null),
      ontbindingsdatum: new FormControl<Date | null>(item?.ontbindingsdatum ?? null),
      onbetaaldGeblevenSchulden: new FormControl<number | null>(item?.onbetaaldGeblevenSchulden ?? null),
    });
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.updateForm(data.aangifte!);
    });
    merge(
      this.ontbindingFg.valueChanges,
    ).pipe(
      debounceTime(500),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = vpbAangifte.specificatieFiscaleEenheid?.specificatieOntbinding || [];
    const formArray = this.ontbindingFg.get('ontbinding') as FormArray;
    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i])
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.ontbindingFg.updateValueAndValidity({emitEvent: false});

    const specificaties: Ontbinding[] = [];

    this.ontbindingFg.getRawValue().ontbinding.forEach(item => {
      const naamDochter = item.naamDochter?.trim();
      if (
        naamDochter &&
        item.ontbindingsdatum != null &&
        item.onbetaaldGeblevenSchulden != null
      ) {
        specificaties.push({
          naamDochter,
          ontbindingsdatum: item.ontbindingsdatum,
          onbetaaldGeblevenSchulden: item.onbetaaldGeblevenSchulden,
        });
      }
    });

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieFiscaleEenheid: {
            ...currentEntity.aangifte.specificatieFiscaleEenheid,
            specificatieOntbinding: specificaties,
          },
        },
      };
    });
  }
}
